/**
 * Scheduled-order selector
 *
 * Chooses WHEN a buy/sell fires. The order itself is untouched — side comes
 * from whichever button you press, quantity and OCO TP/SL from the panel — so
 * there's one order-building path and this only defers it.
 *
 * Lives inside ActionBarTrading, which means it appears in the action bar, the
 * floating desktop panel and the mobile bar from one implementation.
 *
 * When something is queued the chip replaces the picker and counts down, with
 * cancel always one tap away. A pending live order you can't see or stop is
 * the thing to avoid here.
 */

import React, { useEffect, useState } from 'react';
import { Clock, X } from 'lucide-react';
import {
  useScheduledOrderStore,
  ScheduleMode,
} from '../../store/scheduledOrderStore';
import './ScheduleSelector.css';

const MODES: { value: ScheduleMode; label: string }[] = [
  { value: 'now', label: 'Now' },
  { value: 'candle-close', label: 'At candle close' },
  { value: 'before-close', label: 'Before close' },
  { value: 'datetime', label: 'At time' },
];

const ScheduleSelector: React.FC = () => {
  const mode = useScheduledOrderStore((s) => s.mode);
  const leadSeconds = useScheduledOrderStore((s) => s.leadSeconds);
  const atTime = useScheduledOrderStore((s) => s.atTime);
  const pending = useScheduledOrderStore((s) => s.pending);
  const setMode = useScheduledOrderStore((s) => s.setMode);
  const setLeadSeconds = useScheduledOrderStore((s) => s.setLeadSeconds);
  const setAtTime = useScheduledOrderStore((s) => s.setAtTime);
  const cancel = useScheduledOrderStore((s) => s.cancel);

  // Tick once a second so the countdown stays truthful.
  const [, force] = useState(0);
  useEffect(() => {
    if (!pending) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [pending]);

  if (pending) {
    const secs = Math.max(0, Math.ceil((pending.fireAt - Date.now()) / 1000));
    return (
      <div className={`sched-chip ${pending.action === 'Buy' ? 'buy' : 'sell'}`}>
        <Clock size={11} />
        <span className="sched-chip-text">
          {pending.action.toUpperCase()} in {secs}s
        </span>
        <button className="sched-cancel" onClick={cancel} title="Cancel scheduled order">
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className={`sched-wrap ${mode !== 'now' ? 'armed' : ''}`}>
      <Clock size={11} className="sched-icon" />
      <select
        className="sched-select"
        value={mode}
        onChange={(e) => setMode(e.target.value as ScheduleMode)}
        title="When to send the order — side, qty and TP/SL come from this panel"
      >
        {MODES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      {mode === 'before-close' && (
        <input
          className="sched-lead"
          type="number"
          min={0}
          max={120}
          value={leadSeconds}
          onChange={(e) => setLeadSeconds(parseInt(e.target.value, 10) || 0)}
          title="Seconds before the bar closes"
        />
      )}

      {mode === 'datetime' && (
        <input
          className="sched-time"
          type="datetime-local"
          value={atTime}
          onChange={(e) => setAtTime(e.target.value)}
          title="Fire at this time"
        />
      )}
    </div>
  );
};

export default ScheduleSelector;
